import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, CreditCard, Loader2, Pencil } from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "../hooks/useTranslation";
import MembersManagement from "@/components/MembersManagement";
import { BrandCard, BrandStyles, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { GRIGIO, IntestazioneCasella, SCURO } from "../components/integrations/casella";
import { CAMPO_TESTO, Campo } from "../components/widget/campi";

/** Un dato dell'organizzazione: etichetta piccola e valore. */
function Dato({ etichetta, children }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-[#64748B]">{etichetta}</p>
      <div className="mt-1 break-words text-sm font-medium text-[#020617]">{children}</div>
    </div>
  );
}

/** Il nome, che il titolare puo' cambiare sul posto (l'API lo permette solo a lui). */
function NomeOrganizzazione({ tenant }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { refresh } = useAuth();
  const [modifica, setModifica] = useState(false);
  const [nome, setNome] = useState(tenant.name);
  const [salvo, setSalvo] = useState(false);

  const salva = async (e) => {
    e.preventDefault();
    const pulito = nome.trim();
    if (!pulito) {
      toast({ variant: "destructive", title: t("organization.renameEmpty") });
      return;
    }
    if (pulito === tenant.name) {
      setModifica(false);
      return;
    }
    setSalvo(true);
    try {
      await api.put(`/tenants/${tenant._id}`, { name: pulito });
      // L'elenco delle organizzazioni (menu, intestazioni) arriva dalla sessione.
      await refresh?.();
      toast({ variant: "success", title: t("organization.renamed") });
      setModifica(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("organization.renameFailed"),
        description: error.response?.status === 409 ? t("organization.renameDuplicate") : undefined,
      });
    } finally {
      setSalvo(false);
    }
  };

  if (!modifica) {
    return (
      <div className="min-w-0">
        <p className="text-xs font-medium text-[#64748B]">{t("organization.nameLabel")}</p>
        <div className="mt-1 flex items-start gap-1.5">
          <span className="min-w-0 break-words text-sm font-medium text-[#020617]">{tenant.name}</span>
          {tenant.role === "owner" ? (
            <button
              type="button"
              onClick={() => {
                setNome(tenant.name);
                setModifica(true);
              }}
              aria-label={t("organization.rename")}
              title={t("organization.rename")}
              className="-mt-0.5 shrink-0 rounded-[6px] p-1 text-[#94A3B8] transition-colors hover:bg-[#F1F5F9] hover:text-[#334155] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={salva} className="min-w-0 space-y-2 sm:col-span-2">
      <Campo id="nome-organizzazione" etichetta={t("organization.nameLabel")}>
        <input
          id="nome-organizzazione"
          className={CAMPO_TESTO}
          value={nome}
          maxLength={200}
          onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setModifica(false)}
          disabled={salvo}
          autoFocus
        />
      </Campo>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={SCURO} disabled={salvo}>
          {salvo ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
          {t("organization.renameSave")}
        </button>
        <button type="button" className={GRIGIO} onClick={() => setModifica(false)} disabled={salvo}>
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

/**
 * L'organizzazione: i suoi dati e le persone che ci lavorano. Piano e
 * fatturazione stanno nella pagina Abbonamento: qui solo il collegamento.
 */
export default function OrganizationSettings() {
  const { t, i18n } = useTranslation();
  const { activeTenant, canManageBilling } = useTenant();

  if (!activeTenant) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-sm text-[#64748B]">{t("organization.noOrgSelected")}</p>
      </div>
    );
  }

  const creata = activeTenant.createdAt
    ? new Date(activeTenant.createdAt).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" })
    : "—";
  const ruolo = ["owner", "admin", "member"].includes(activeTenant.role) ? activeTenant.role : null;

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />
      <IntestazioneCasella
        icona={Building2}
        titolo={t("common.organization")}
        dettagli={<span className="truncate text-sm text-[#475569]">{activeTenant.name}</span>}
      />

      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-10 pb-10">
        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-[#020617]">{t("organization.dataTitle")}</h2>
            <p className="mt-0.5 max-w-2xl text-sm text-[#64748B]">{t("organization.dataDescription")}</p>
          </div>
          <BrandCard className="grid gap-5 p-5 sm:grid-cols-2 lg:grid-cols-4">
            <NomeOrganizzazione key={activeTenant._id} tenant={activeTenant} />
            <Dato etichetta={t("organization.yourRole")}>
              {ruolo ? t(`components.members.${ruolo}`) : "—"}
            </Dato>
            <Dato etichetta={t("organization.createdOn")}>{creata}</Dato>
            <Dato etichetta={t("organization.idLabel")}>
              <span className="select-all break-all font-mono text-xs font-normal text-[#475569]">{activeTenant._id}</span>
            </Dato>
          </BrandCard>
        </section>

        <MembersManagement tenantId={activeTenant._id} />

        {canManageBilling ? (
          <BrandCard className="flex flex-wrap items-center gap-4 p-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F1F5F9]" aria-hidden="true">
              <CreditCard className="h-4 w-4 text-[#334155]" />
            </span>
            <div className="min-w-[12rem] flex-1">
              <p className="text-sm font-semibold text-[#020617]">{t("organization.billingLinkTitle")}</p>
              <p className="mt-0.5 text-sm text-[#64748B]">{t("organization.billingLinkText")}</p>
            </div>
            <Link to="/billing" className={GRIGIO}>
              {t("organization.billingLinkCta")}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </BrandCard>
        ) : null}
      </div>
    </div>
  );
}
